// Naming Conventions
// pointer values format {keyPointer}{dimension}Values Ex: sumRowValues, sourceColValues
// pointer Meta Data {keyPointer}{dimension}MetaData Ex: sumLevelColMetadata, sumRowMetadata

/**
 * @category Corners
 * @module FormulaCorner
 */
import * as CornerTypes from "../types/CornerTypes";
import * as CommonTypes from "../types/CommonTypes";
import * as VuexTypes from "../types/VuexTypes";

import { rangeIndexToLabel } from "../../components/process-design/cell/Utils";
import { hfInstance } from "../Cells";
import { FORMULA_KEY_POINTERS } from "./constants";
import { getMatrixOfValue, unifyRanges } from "../../utils/util";
import { combineChangedRowsAndColumns } from "./StyleCorner";
const requiredFormulaAreaHeaders = ["id", "active"];

/**
 * Applies calculated sum-level data to the (hf) instance.
 *
 * After the sum-level data for the `TARGET` key pointer column index is computed,
 * this function applies that data to other relevant columns defined by `TARGET` row key pointer.
 *
 * @param {CornerTypes.Corner} corner - The formula corner containing metadata
 * @param {CornerTypes.SumLevelCalc[]} sumLevelCalcs - The computed sum-level values to apply.
 */
function addSumFormulasToHf(corner, sumLevelCalcs) {
  const activeRowMetaData = corner.rowKeyPointers[FORMULA_KEY_POINTERS.ACTIVE][0];
  const { sheetHfId, rowRange } = corner;

  const startColIndex = rowRange[2];
  const endColIndex = activeRowMetaData.range[2];

  const activeRowValues = hfInstance.getRangeValues({
    start: { sheet: sheetHfId, col: startColIndex, row: activeRowMetaData.range[1] },
    end: { sheet: sheetHfId, col: endColIndex, row: activeRowMetaData.range[1] },
  })[0];
  const ranges = [];
  hfInstance.suspendEvaluation();
  sumLevelCalcs.forEach((sumLevelCalc) => {
    activeRowValues.forEach((activeRowValue, activeRowIndex) => {
      if (!activeRowValue) return;

      const vals = sumLevelCalc.parameters?.map((item) =>
        rangeIndexToLabel([activeRowIndex + startColIndex, item[1], activeRowIndex + startColIndex, item[3]])
      );
      if (vals.length === 0) return;

      hfInstance.setCellContents(
        { sheet: sheetHfId, col: activeRowIndex + startColIndex, row: sumLevelCalc.range[1] },
        null
      );
      ranges.push([
        activeRowIndex + startColIndex,
        sumLevelCalc.range[1],
        activeRowIndex + startColIndex,
        sumLevelCalc.range[1],
      ]);

      hfInstance.setCellContents(
        { sheet: sheetHfId, col: activeRowIndex + startColIndex, row: sumLevelCalc.range[1] },
        "=sum(" + vals.join(",") + ")"
      );
    });
  });
  hfInstance.resumeEvaluation();
  return ranges;
}

// the ranges are on sum level column, later they are changed to column where the get applied [sumLevelCol, row, sumLevelCol, row]

/**
 * Calculates the data necessary for sum level formula calculations.
 *
 * For sum level formulas, there are multiple levels:
 * - Level 0: The data remains unchanged.
 * - Level 1: The sum of all previous Level 0 values up to the previous Level 1.
 * - Level 2: The sum of all previous Level 1 values up to the previous Level 2.
 * - ...
 *
 * This function identifies cells with a level greater than 0, where sum data needs to be applied,
 * and generates the required parameters for those calculations.
 *
 * @param {CornerTypes.Corner} corner - The corner object containing the necessary data for calculation.
 * @param {number} rowYPosition - The starting point of the data, defined by the row Column Key Pointer.
 * @returns {CornerTypes.SumLevelCalc[]} An array of objects, each representing a cell where a sum formula should be applied.
 *  - `range`: Specifies the cell range where the sum should be applied.
 *  - `parameters`: Parameters to be used in the sum function (e.g., `sum(p21:p24)`).
 */

function calculateSumLevels(corner, rowYPosition) {
  const sumLevelColMetaData = corner["columnKeyPointers"][FORMULA_KEY_POINTERS.SUM_LEVEL][0];

  const map = {};
  /** @type { CornerTypes.SumLevelCalc[]} */
  const sumLevelCalcs = [];
  const { sheetHfId } = corner;

  const sumLevelColValues = hfInstance.getRangeValues({
    start: { sheet: sheetHfId, col: sumLevelColMetaData.range[0], row: rowYPosition },
    end: { sheet: sheetHfId, col: sumLevelColMetaData.range[0], row: sumLevelColMetaData.range[3] },
  });

  sumLevelColValues.forEach((sumLevelValue, index) => {
    sumLevelValue = sumLevelValue[0];
    /** @type { CommonTypes.Range} */
    const sumLevelRange = [
      sumLevelColMetaData.range[0],
      index + rowYPosition,
      sumLevelColMetaData.range[0],
      index + rowYPosition,
    ];
    if (sumLevelValue === 0) {
      if (!(sumLevelValue in map)) map[0] = [sumLevelRange];
      else map[0] = [unifyRanges(map[0][0], sumLevelRange)];
    } else if (sumLevelValue > 0) {
      if (!(sumLevelValue in map)) map[sumLevelValue] = [];
      map[sumLevelValue].push(sumLevelRange);
      let parameters = [];

      if (map[sumLevelValue - 1]) parameters = [...map[sumLevelValue - 1]];

      sumLevelCalcs.push({
        range: sumLevelRange,
        parameters,
      });
      delete map[sumLevelValue - 1];
    }
  });
  return sumLevelCalcs;
}
/**
 * @description
 * Aggregates all changed row or column indexes from a set of key pointers.
 * Useful when you want to merge changes across multiple key pointers for a specific dimension.
 *
 * @param {CornerTypes.KeyPointerChangeMap} data - Map of key pointers and their change data.
 * @param {"row" | "column"} dimension - The dimension to collect changes for.
 * @param {string[]} keyPointers - Array of key pointer names to include in the merge.
 * @returns {Set<number>} A set of unique changed indexes.
 */
function collectChangedIndexes(data, dimension, keyPointers) {
  /** @type {number[]} */
  const indexes = [];
  keyPointers.forEach((keyPointer) => {
    if (data[keyPointer])
      Object.entries(data[keyPointer]).forEach(([key, value]) => {
        indexes.push(...(dimension === "column" ? value.column : value.row));
      });
  });
  return new Set(indexes);
}

const initialState = {
  areas: {},
  sourceRowIndexMap: {},
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {},
  actions: {
    /**
     * @function
     * @name module:FormulaCorner.loadFormulaAreas
     * @description Loads the formula area table.
     *
     * The formula area includes the following columns:
     * - `id`: Identifier for the formula area.
     * - `active`: Flag indicating whether the formula area is active.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     */
    loadFormulaAreas({ commit, dispatch, state, rootState, getters, rootGetters }) {
      const isFormulaAreaTableValid = rootGetters["corners/isTableValid"]("FormulaAreas", requiredFormulaAreaHeaders);
      if (!isFormulaAreaTableValid) return;

      const headers = rootGetters["corners/getNamedExpressionAsArray"]("FormulaAreas..header")[0];

      const headerMap = {};

      headers.forEach((item, index) => {
        headerMap[String(item).toLowerCase()] = index;
      });
      state.areas = {};
      const data = rootGetters["corners/getNamedExpressionAsArray"]("FormulaAreas..data");
      data.forEach((row) => {
        const id = row[headerMap["id"]];
        const active = row[headerMap["active"]];
        if (!id || !active) return;
        state.areas[id] = {
          active,
        };
      });
    },
    /**
     * @function
     * @name module:FormulaCorner.applyFormulas
     * @description Determines which formula corner to apply based on priority.
     *
     * There are three types of formula key pointers, applied in the following order of priority:
     * 1. Sum level key pointer (highest priority)
     * 2. Source-target key pointer
     * 3. Formula key pointer (lowest priority)
     *
     * If all three key pointers are valid, only the one with the highest priority will be applied.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying formulas.
     * @param {string} param1.area - The area where the formula is being applied.
     * @param {CornerTypes.KeyPointerChangeMap} param1.data - The key pointer changes.
     */
    async applyFormulas({ commit, state, rootState, dispatch, getters, rootGetters }, { area, data }) {
      if (getters.isSumLevelKeyPointerValid(area)) {
        // Row and column changes are not needed for sum level,
        // as it is always re-applied fully from the beginning.
        dispatch("applySumLevelKeyPointer", { area });
      } else if (
        getters.isSourceTargetKeyPointerValid(area) &&
        (data[FORMULA_KEY_POINTERS.TARGET] || data[FORMULA_KEY_POINTERS.ACTIVE] || data[FORMULA_KEY_POINTERS.SOURCE])
      ) {
        dispatch("loadSourceMetaData", { area });
        dispatch("applySourceTargetKeyPointer", { area, data });
      } else if (getters.isFormulaKeyPointerValid(area))
        if (data[FORMULA_KEY_POINTERS.START])
          // If the row position changes, apply the formula corner for the entire dataset.
          // TODO: Optimize by applying the formula only to the range between the last and current row positions.
          dispatch("startFormulaKeyPointer", { area });
        else dispatch("applyFormulaKeyPointer", { area, data });

      dispatch("rangeStyle/persist", null, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
    },
    /**
     * @function
     * @name module:FormulaCorner.applyFormulaKeyPointer
     * @description Applies the formula key pointer logic for the given corner.
     *
     * This action is responsible for handling changes related to the formula key pointer type.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying the formula key pointer.
     * @param {string} param1.area - The corner ID where the formula key pointer should be applied.
     * @param {CornerTypes.KeyPointerChangeMap} param1.data - The key pointer changes.
     */
    applyFormulaKeyPointer({ commit, state, rootState, dispatch, getters, rootGetters }, { area, data }) {
      const changes = combineChangedRowsAndColumns(data);
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const { sheetHfId, rowRange } = corner;
      const rowYPosition = getters.startRowPosition(area);
      const formulaRowKeyPointer = corner["rowKeyPointers"][FORMULA_KEY_POINTERS.FORMULA][0];
      const formulaRowKeyPointerRange = formulaRowKeyPointer.range;
      const ranges = [];
      const activeRowMetaData =
        corner["rowKeyPointers"][FORMULA_KEY_POINTERS.ACTIVE] &&
        corner["rowKeyPointers"][FORMULA_KEY_POINTERS.ACTIVE][0];
      const activeColumnIndices = activeRowMetaData && getters.activeColumnIndexes(sheetHfId, activeRowMetaData);
      changes.column.forEach((columnIndex) => {
        if (activeColumnIndices && !activeColumnIndices.has(columnIndex)) return;
        const result = hfInstance.getFillRangeData(
          {
            start: { sheet: sheetHfId, col: columnIndex, row: formulaRowKeyPointerRange[1] },
            end: { sheet: sheetHfId, col: columnIndex, row: formulaRowKeyPointerRange[1] },
          },
          {
            start: { sheet: sheetHfId, col: columnIndex, row: rowYPosition },
            end: { sheet: sheetHfId, col: columnIndex, row: rowRange[3] },
          }
        );
        ranges.push([columnIndex, rowYPosition, columnIndex, rowRange[3]]);
        hfInstance.setCellContents(
          { sheet: sheetHfId, col: columnIndex, row: rowYPosition },
          getMatrixOfValue([columnIndex, rowYPosition, columnIndex, rowRange[3]], null)
        );
        hfInstance.setCellContents({ sheet: sheetHfId, col: columnIndex, row: rowYPosition }, result);
      });
      dispatch("cells/persistRanges", ranges, { root: true });
    },
    /**
     * @function
     * @name module:FormulaCorner.startFormulaKeyPointer
     * @description Applies the formula key pointer logic for the entire dataset of the given corner.
     *
     * This is similar to `applyFormulaKeyPointer`, but it processes the full dataset
     * rather than responding to changes.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying the formula key pointer.
     * @param {string} param1.area - The corner ID where the formula key pointer should be applied.
     */
    startFormulaKeyPointer({ commit, state, rootState, dispatch, getters, rootGetters }, { area }) {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const { sheetHfId, columnRange, rowRange } = corner;
      const rowYPosition = getters.startRowPosition(area);
      const formulaRowKeyPointer = corner["rowKeyPointers"][FORMULA_KEY_POINTERS.FORMULA][0];
      const formulaRowKeyPointerRange = formulaRowKeyPointer.range;

      const values = hfInstance.getRangeSerialized({
        start: { sheet: sheetHfId, col: rowRange[2], row: formulaRowKeyPointerRange[1] },
        end: { sheet: sheetHfId, col: columnRange[2], row: formulaRowKeyPointerRange[3] },
      })[0];
      const ranges = [];
      const activeRowMetaData =
        corner["rowKeyPointers"][FORMULA_KEY_POINTERS.ACTIVE] &&
        corner["rowKeyPointers"][FORMULA_KEY_POINTERS.ACTIVE][0];
      const activeColumnIndices = activeRowMetaData && getters.activeColumnIndexes(sheetHfId, activeRowMetaData);

      values.forEach((value, index) => {
        const columnIndex = index + rowRange[2];
        if (!value || (activeColumnIndices && !activeColumnIndices.has(columnIndex))) return;
        const result = hfInstance.getFillRangeData(
          {
            start: { sheet: sheetHfId, col: columnIndex, row: formulaRowKeyPointerRange[1] },
            end: { sheet: sheetHfId, col: columnIndex, row: formulaRowKeyPointerRange[1] },
          },
          {
            start: { sheet: sheetHfId, col: columnIndex, row: rowYPosition },
            end: { sheet: sheetHfId, col: columnIndex, row: rowRange[3] },
          }
        );
        ranges.push([columnIndex, rowYPosition, columnIndex, rowRange[3]]);
        hfInstance.setCellContents({ sheet: sheetHfId, col: columnIndex, row: rowYPosition }, result);
      });
      dispatch("cells/persistRanges", ranges, { root: true });
    },
    /**
     * @function
     * @name module:FormulaCorner.applySourceTargetKeyPointer
     * @description Applies the source key pointer logic for the given corner.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying the formula key pointer.
     * @param {string} param1.area - The corner ID where the formula key pointer should be applied.
     * @param {CornerTypes.KeyPointerChangeMap} param1.data - The key pointer changes.
     */
    applySourceTargetKeyPointer({ commit, state, rootState, dispatch, getters, rootGetters }, { area, data }) {
      const columnChanges = collectChangedIndexes(data, "column", [
        FORMULA_KEY_POINTERS.ACTIVE,
        FORMULA_KEY_POINTERS.SOURCE,
      ]);
      const targetKeyPointerChanges = data[FORMULA_KEY_POINTERS.TARGET] && data[FORMULA_KEY_POINTERS.TARGET][0];
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const { sheetHfId, columnRange, rowRange } = corner;
      const rowYPosition = getters.startRowPosition(area);
      const targetColumnMetaData = corner["columnKeyPointers"][FORMULA_KEY_POINTERS.TARGET][0];
      const activeRowMetaData = corner["rowKeyPointers"][FORMULA_KEY_POINTERS.ACTIVE][0];
      const targetColumnRange = targetColumnMetaData.range;
      const activeColumnIndices = getters.activeColumnIndexes(sheetHfId, activeRowMetaData);
      const ranges = [];
      columnChanges.forEach((columnIndex) => {
        if (!activeColumnIndices.has(columnIndex)) return;
        for (let i = rowYPosition; i <= rowRange[3]; i++) {
          const value = hfInstance.getCellValue({ sheet: sheetHfId, col: targetColumnRange[0], row: i });
          const rowIndex = state.sourceRowIndexMap[value];
          if (!rowIndex) continue;
          const result = hfInstance.getFillRangeData(
            {
              start: { sheet: sheetHfId, col: columnIndex, row: rowIndex },
              end: { sheet: sheetHfId, col: columnIndex, row: rowIndex },
            },
            {
              start: { sheet: sheetHfId, col: columnIndex, row: i },
              end: { sheet: sheetHfId, col: columnIndex, row: i },
            }
          );
          ranges.push([columnIndex, i, columnIndex, i]);
          hfInstance.setCellContents({ sheet: sheetHfId, col: columnIndex, row: i }, null);
          hfInstance.setCellContents({ sheet: sheetHfId, col: columnIndex, row: i }, result);
        }
      });

      targetKeyPointerChanges?.row.forEach((rowIndex) => {
        if (rowIndex < rowYPosition) return;
        // i = column index
        for (let i = rowRange[2]; i <= columnRange[2]; i++) {
          const value = hfInstance.getCellValue({ sheet: sheetHfId, col: targetColumnRange[0], row: rowIndex });
          const keyRowIndex = state.sourceRowIndexMap[value];
          if (!keyRowIndex || !activeColumnIndices.has(i)) continue;
          const result = hfInstance.getFillRangeData(
            {
              start: { sheet: sheetHfId, col: i, row: keyRowIndex },
              end: { sheet: sheetHfId, col: i, row: keyRowIndex },
            },
            {
              start: { sheet: sheetHfId, col: i, row: rowIndex },
              end: { sheet: sheetHfId, col: i, row: rowIndex },
            }
          );
          if (!result || !result[0][0]) continue;
          ranges.push([i, rowIndex, i, rowIndex]);

          hfInstance.setCellContents({ sheet: sheetHfId, col: i, row: rowIndex }, null);
          hfInstance.setCellContents({ sheet: sheetHfId, col: i, row: rowIndex }, result);
        }
      });
      dispatch("cells/persistRanges", ranges, { root: true });
    },
    /**
     * @function
     * @name module:FormulaCorner.applySumLevelKeyPointer
     * @description Applies the `SUMLEVEL` key pointer logic for the given corner.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying the `SUMLEVEL` key pointer.
     * @param {string} param1.area - The corner ID where the `SUMLEVEL` key pointer should be applied.
     *
     */
    applySumLevelKeyPointer({ commit, state, rootState, dispatch, getters, rootGetters }, { area }) {
      const corner = rootState.corners.corners[area];
      const rowYPosition = getters.startRowPosition(area);

      const sumLevelCalcs = calculateSumLevels(corner, rowYPosition);
      const ranges = addSumFormulasToHf(corner, sumLevelCalcs);

      dispatch("cells/persistRanges", ranges, { root: true });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
    },
    /**
     * @function
     * @name module:FormulaCorner.loadSourceMetaData
     * @description Loads source metadata for the SOURCE-TARGET formula corner.
     *
     * In this corner, users can select a formula based on a key.
     * For example:
     * - 'A' could represent a sum formula.
     * - 'B' could represent an average formula.
     *
     * This function retrieves the row indexes associated with each key.
     *
     * @param {VuexTypes.VuexContext} context - The Vuex action context object.
     * @param {Object} param1 - Parameters for applying the `SOURCE-TARGET` key pointer.
     * @param {string} param1.area - The corner ID where the `SOURCE-TARGET` key pointer should be applied.
     */
    loadSourceMetaData({ commit, state, rootState, dispatch, getters, rootGetters }, { area }) {
      /**@type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const { sheetHfId } = corner;
      const sourceColumnKeyPointer = corner["columnKeyPointers"][FORMULA_KEY_POINTERS.SOURCE][0];
      const sourceRowKeyPointers = corner["rowKeyPointers"][FORMULA_KEY_POINTERS.SOURCE];
      const sourceRowIndices = new Set(sourceRowKeyPointers.map((item) => item.range[1]));

      const values = hfInstance.getRangeValues({
        start: {
          sheet: sheetHfId,
          col: sourceColumnKeyPointer.range[0],
          row: sourceColumnKeyPointer.range[1],
        },
        end: {
          sheet: sheetHfId,
          col: sourceColumnKeyPointer.range[2],
          row: sourceColumnKeyPointer.range[3],
        },
      });
      const map = {};
      values.forEach((value, index) => {
        const rowIndex = index + sourceColumnKeyPointer.range[1];
        if (!sourceRowIndices.has(rowIndex)) return;
        value = value[0];
        if (value) {
          map[value] = rowIndex;
        }
      });
      state.sourceRowIndexMap = map;
    },
    async resetState({ commit, state, rootState, dispatch }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    /**
     * @callback FormulaAreaResolver
     * @returns {Set<string>} An array of corner IDs representing active formula areas.
     */
    /**
     * @function
     * @name module:FormulaCorner.activeFormulaAreas
     * @description Returns all active formula areas.
     *
     * A formula area is considered active if its `active` flag is set to `true`.
     *
     * @returns {FormulaAreaResolver} A function that resolves active formula areas.
     */
    activeFormulaAreas: (state, getters, rootState) => () => {
      return new Set(
        Object.keys(state.areas)
          .filter((item) => state.areas[item].active)
          .map((item) => item.toLowerCase())
      );
    },
    /**
     * @callback ColumnIndexResolver
     * @param {string} sheetHfId - The sheet identifier.
     * @param { CornerTypes.KeyPointer} targetRowMetaData - Row metadata to resolve indexes for.
     * @returns {Set<number>} An array of resolved column indexes.
     */

    /**
     * @function
     * @name module:FormulaCorner.activeColumnIndexes
     * @description Returns all column indexes that are considered active (i.e., have values other than 0 or empty).
     *
     * These are the columns indices that will be changed, and are used specifically for the source-target formula corner.
     *
     * @returns { ColumnIndexResolver }
     */
    activeColumnIndexes: (state, getters, rootState) => (sheetHfId, activeRowMetaData) => {
      const activeIndices = new Set();

      const targetRowValues = hfInstance.getRangeValues({
        start: {
          sheet: sheetHfId,
          col: activeRowMetaData.range[0],
          row: activeRowMetaData.range[1],
        },
        end: {
          sheet: sheetHfId,
          col: activeRowMetaData.range[2],
          row: activeRowMetaData.range[3],
        },
      });
      targetRowValues[0].forEach((item, index) => {
        if (item) activeIndices.add(index + activeRowMetaData.range[0]);
      });

      return activeIndices;
    },
    /**
     * @callback StartRowResolver
     * @param {string} area - The corner ID (e.g. "Style_01", "Formula_02").
     * @returns {number} The starting row index of the data section for the given corner.
     */

    /**
     * @function
     * @name module:FormulaCorner.startRowPosition
     * @description Gets the row index where the actual data starts for the given corner.
     *
     * All rows before this index are reserved for corner (meta) data.
     *
     * @returns { StartRowResolver}
     * A function that takes a corner ID and returns the starting row index of the data section.
     */
    startRowPosition: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      const columnKeyPointers = corner.columnKeyPointers;
      const rowKeyPointer = columnKeyPointers[FORMULA_KEY_POINTERS.START][0];
      const range = rowKeyPointer.range;

      const data = hfInstance.getRangeValues({
        start: { sheet: corner.sheetHfId, col: range[0], row: range[1] },
        end: { sheet: corner.sheetHfId, col: range[2], row: range[3] },
      });
      return Math.max(data.findIndex((item) => !!item[0]) + range[1], corner.columnRange[3] + 1);
    },
    /**
     * @callback SumLevelValidator
     * @param {string} area - The corner ID to validate (e.g., "Formula_01" or "Style_02").
     * @returns {boolean} Returns `true` if the corner has valid sum level configuration, otherwise `false`.
     */
    /**
     * @function
     * @name module:FormulaCorner.isSumLevelKeyPointerValid
     * @description Checks if the sum level key pointer is valid for the given area.
     *
     * For a sum level key pointer to be considered valid, the corner must include:
     * - `SUMLEVEL` and `START` in the column key pointers
     * - `ACTIVE` in the row key pointers
     *
     * @returns { SumLevelValidator}
     * A function that takes a corner ID and returns `true` if the sum level key pointer is valid; otherwise, `false`.
     */
    isSumLevelKeyPointerValid: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      return (
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.START] &&
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.SUM_LEVEL] &&
        corner.rowKeyPointers[FORMULA_KEY_POINTERS.ACTIVE]
      );
    },
    /**
     * @callback SourceTargetValidator
     * @param {string} area - The corner ID to validate (e.g., "Formula_01", "Style_02").
     * @returns {boolean} Returns `true` if the corner has a valid source-target key pointer configuration.
     */
    /**
     * @function
     * @name module:FormulaCorner.isSourceTargetKeyPointerValid
     * @description Checks if the source-target key pointer is valid for the given area.
     *
     * For a source-target key pointer to be valid, the corner must include:
     * - `SOURCE` and `TARGET` in column key pointers
     * - `TARGET` in row key pointers
     * - A `START` column key pointer
     *
     *  * @returns {SourceTargetValidator}
     * A function that takes a corner ID and returns `true` if the source-target key pointer is valid; otherwise, `false`.
     */
    isSourceTargetKeyPointerValid: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      return (
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.START] &&
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.SOURCE] &&
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.TARGET] &&
        corner.rowKeyPointers[FORMULA_KEY_POINTERS.ACTIVE] &&
        corner.rowKeyPointers[FORMULA_KEY_POINTERS.SOURCE]
      );
    },
    /**
     * @callback FormulaKeyPointerValidator
     * @param {string} area - The corner ID to validate (e.g., "Formula_01", "Style_03").
     * @returns {boolean} Returns `true` if the formula key pointer setup is valid, otherwise `false`.
     */
    /**
     * @function
     * @name module:FormulaCorner.isFormulaKeyPointerValid
     * @description Checks if the formula key pointer is valid for the given area.
     *
     * For a formula key pointer to be considered valid, it must include:
     * - A `START` key in the column key pointers
     * - A `FORMULA` key in the row key pointers
     *
     * @returns {FormulaKeyPointerValidator}
     * A function that takes a corner ID and returns `true` if the formula key pointer is valid; otherwise, `false`.
     */
    isFormulaKeyPointerValid: (state, getters, rootState) => (area) => {
      /** @type {CornerTypes.Corner} */
      const corner = rootState.corners.corners[area];
      return (
        corner.columnKeyPointers[FORMULA_KEY_POINTERS.START] && corner.rowKeyPointers[FORMULA_KEY_POINTERS.FORMULA]
      );
    },
  },
};
